import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import {
  emptyTutorialState,
  loadTutorialState,
  saveTutorialState,
} from "@/tutorials/tutorialStateStorage";
import SystemSettingsService from "@/Service/SystemSettingsService";
import { showAlert } from "./alertSlice";

// Admin config (env-wide, system_settings row "tutorials_config"): which
// tutorials are hidden from contributors and which never trigger the
// post-login prompt. Cached in localStorage so the boot decision can use the
// last known value if the fetch is slow/offline.
const CONFIG_CACHE_KEY = "clipn:tutorials-config-v1";

export const emptyTutorialsConfig = () => ({ hidden: [], noPrompt: [], mandatory: [] });

const normalizeConfig = (raw) => {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }
  const arr = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);
  return { hidden: arr(parsed?.hidden), noPrompt: arr(parsed?.noPrompt), mandatory: arr(parsed?.mandatory) };
};

const readCachedConfig = () => {
  try {
    const raw = window.localStorage.getItem(CONFIG_CACHE_KEY);
    return raw ? normalizeConfig(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedConfig = (config) => {
  try {
    window.localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(config));
  } catch {
    /* ignore */
  }
};

// Per-user "which tutorials has this person completed/skipped" state plus the
// "don't show tips" preference. Persistence lives behind
// tutorialStateStorage.js so the backend swap doesn't touch this slice.

const initialState = {
  uid: null,
  ...emptyTutorialState(),
  loaded: false,
  error: null,
  config: readCachedConfig() || emptyTutorialsConfig(),
  configLoaded: false,
  configSaving: false,
  configLastRequestId: null,
};

export const fetchTutorialState = createAsyncThunk(
  "tutorials/fetchState",
  async (uid, { rejectWithValue }) => {
    try {
      const state = await loadTutorialState(uid);
      return { uid, state };
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load tutorial state");
    }
  }
);

export const markTutorial = createAsyncThunk(
  "tutorials/mark",
  async ({ tutorialId, status, version }, { getState }) => {
    const current = getState().tutorials;
    const next = {
      byId: {
        ...current.byId,
        [tutorialId]: {
          status,
          versionSeen: Number(version) || 1,
          updatedAt: new Date().toISOString(),
        },
      },
      preferences: { ...current.preferences },
    };
    await saveTutorialState(current.uid, next, {
      type: "tutorial",
      tutorialId,
      status,
      version: Number(version) || 1,
    });
    return next;
  }
);

export const setSuppressPrompts = createAsyncThunk(
  "tutorials/setSuppressPrompts",
  async (suppressPrompts, { getState }) => {
    const current = getState().tutorials;
    const next = {
      byId: { ...current.byId },
      preferences: { ...current.preferences, suppressPrompts: !!suppressPrompts },
    };
    await saveTutorialState(current.uid, next, {
      type: "preferences",
      suppressPrompts: !!suppressPrompts,
    });
    return next;
  }
);

export const fetchTutorialsConfig = createAsyncThunk(
  "tutorials/fetchConfig",
  async (_, { rejectWithValue }) => {
    try {
      const data = await SystemSettingsService.getTutorialsConfig();
      return normalizeConfig(data?.value);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load tutorials config");
    }
  }
);

export const updateTutorialsConfig = createAsyncThunk(
  "tutorials/updateConfig",
  async (config, { dispatch, rejectWithValue }) => {
    try {
      const data = await SystemSettingsService.updateTutorialsConfig(normalizeConfig(config));
      return normalizeConfig(data?.value);
    } catch (error) {
      const status = error?.response?.status;
      dispatch(
        showAlert(
          "error",
          status === 403 ? "You don't have permission to change tutorials." : "Failed to save tutorial settings. Please retry."
        )
      );
      // Re-sync with the server instead of guessing which optimistic flips to
      // roll back (several switches may be in flight).
      dispatch(fetchTutorialsConfig());
      return rejectWithValue(error?.message || "Failed to update tutorials config");
    }
  }
);

const applyState = (state, payload) => {
  state.byId = payload.byId || {};
  state.preferences = {
    suppressPrompts: !!payload.preferences?.suppressPrompts,
  };
};

const tutorialsSlice = createSlice({
  name: "tutorials",
  initialState,
  reducers: {
    resetTutorialState: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      // Defensive: a sign-out drops the previous person's per-user state so a
      // different account on the same browser never reads/writes it. The
      // env-wide admin config is kept (it isn't per user).
      .addCase("auth/signOut/fulfilled", (state) => {
        state.uid = null;
        state.byId = {};
        state.preferences = { suppressPrompts: false };
        state.loaded = false;
        state.error = null;
      })
      .addCase(fetchTutorialState.pending, (state, action) => {
        // Switching user → drop the previous user's state immediately.
        if (state.uid !== action.meta.arg) {
          state.uid = action.meta.arg;
          state.byId = {};
          state.preferences = { suppressPrompts: false };
          state.loaded = false;
        }
        state.error = null;
      })
      .addCase(fetchTutorialState.fulfilled, (state, action) => {
        state.uid = action.payload.uid;
        applyState(state, action.payload.state);
        state.loaded = true;
      })
      .addCase(fetchTutorialState.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "error";
        // Still mark loaded so the UI is usable; empty state == "new user".
        state.loaded = true;
      })
      .addCase(markTutorial.fulfilled, (state, action) => {
        applyState(state, action.payload);
      })
      .addCase(setSuppressPrompts.fulfilled, (state, action) => {
        applyState(state, action.payload);
      })
      .addCase(fetchTutorialsConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        state.configLoaded = true;
        writeCachedConfig(action.payload);
      })
      .addCase(fetchTutorialsConfig.rejected, (state) => {
        // Fall back to the cached copy (already in state); still usable.
        state.configLoaded = true;
      })
      .addCase(updateTutorialsConfig.pending, (state, action) => {
        // Optimistic: flip the switch immediately. Latest request wins.
        state.config = normalizeConfig(action.meta.arg);
        state.configSaving = true;
        state.configLastRequestId = action.meta.requestId;
      })
      .addCase(updateTutorialsConfig.fulfilled, (state, action) => {
        // Ignore stale responses (an older save resolving after a newer one).
        if (action.meta.requestId === state.configLastRequestId) {
          state.config = action.payload;
          state.configSaving = false;
          writeCachedConfig(action.payload);
        }
      })
      .addCase(updateTutorialsConfig.rejected, (state, action) => {
        if (action.meta.requestId === state.configLastRequestId) state.configSaving = false;
      });
  },
});

export const { resetTutorialState } = tutorialsSlice.actions;

export const useTutorialsState = () => useSelector((state) => state.tutorials);

export const useTutorialsActions = () => {
  const dispatch = useDispatch();
  return useMemo(
    () => ({
      fetchTutorialState: (uid) => dispatch(fetchTutorialState(uid)),
      markTutorial: (payload) => dispatch(markTutorial(payload)),
      setSuppressPrompts: (value) => dispatch(setSuppressPrompts(value)),
      resetTutorialState: () => dispatch(resetTutorialState()),
      fetchTutorialsConfig: () => dispatch(fetchTutorialsConfig()),
      updateTutorialsConfig: (config) => dispatch(updateTutorialsConfig(config)),
    }),
    [dispatch]
  );
};

export default tutorialsSlice.reducer;
